import React, {useCallback, useContext} from 'react';


import {ConfirmationModal} from 'src/components/confirmation-modal';
import {NumberInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {Skeleton} from 'src/components/skeleton';
import {CrmType as CrmTypes} from 'src/openapi/admin/models/CrmType';

import Button from '@pb/kit-component-button';
import FormSection from '@pb/kit-component-form-section';
import {Integer} from '@pb/kit-component-formatters';
import {LocaleContext} from '@pb/kit-context-locale';

import {CommonEntitiesSection} from '../../components/common-entities-section';

import {HubspotSectionSkeleton} from './hubspot-skeleton';

import type {Error} from 'src/openapi/admin/models/Error';
import type {GetCrmLimit} from 'src/openapi/admin/models/GetCrmLimit';
import type {HubspotAccountSettings} from 'src/openapi/admin/models/HubspotAccountSettings';
import type {LinkageStatistics} from 'src/openapi/admin/models/LinkageStatistics';

type Props = {
    isLoading: boolean,
    isLinkageStatisticLoading: boolean,
    isSyncLinkageLoading: boolean,
    isAuthRedirectUrlLoading: boolean,
    linkageStatisticLoadingError: Error | null,
    revokeTokenBusy: boolean,

    limits: GetCrmLimit,
    settings: HubspotAccountSettings,
    linkageStatistic: LinkageStatistics,
    orgId?: string,

    onChangeLimits: React.Dispatch<React.SetStateAction<GetCrmLimit>>,
    onChangeSettings: React.Dispatch<React.SetStateAction<HubspotAccountSettings>>,
    onSyncLinkageStatistic: () => void,
    onSetupOAuthLogin: () => void,
    onRevokeOAuthToken: () => void,

    disabled?: boolean,
    readonly?: boolean,
};

export const HubspotSection = (props: Props) => {
    const {
        isLoading,
        isSyncLinkageLoading,
        isLinkageStatisticLoading,
        isAuthRedirectUrlLoading,
        linkageStatisticLoadingError,
        revokeTokenBusy,

        limits,
        settings,
        linkageStatistic,
        orgId,

        onChangeLimits,
        onChangeSettings,
        onSyncLinkageStatistic,
        onSetupOAuthLogin,
        onRevokeOAuthToken,
        disabled,
        readonly,
    } = props;
    const {locale} = useContext(LocaleContext);

    const changeMaxUsers = useCallback((maxUsers?: number) => {
        onChangeLimits(prevState => ({...prevState, maxUsers: maxUsers || 0}));
    }, [onChangeLimits]);

    const changeMaxImports = useCallback((maxImports?: number) => {
        onChangeLimits(prevState => ({...prevState, maxImports: maxImports || 0}));
    }, [onChangeLimits]);

    const changeImportCredits = useCallback((importCredits?: number) => {
        onChangeLimits({...limits, importCredits: importCredits || 0});
    }, [limits, onChangeLimits]);

    return (
        <Skeleton shown={isLoading} view={<HubspotSectionSkeleton/>}>
            <FormSection headline="Credentials">
                <ResponsiveElementGroup>
                    <Button
                        disabled={disabled || readonly}
                        busy={isAuthRedirectUrlLoading}
                        variant="Type.SECONDARY"
                        onClick={onSetupOAuthLogin}>
                        Setup OAuth login
                    </Button>
                    <ConfirmationModal
                        title="Revoke OAuth token"
                        busy={revokeTokenBusy}
                        message="
                        This will remove the connection between the PitchbBook Plugin and HubSpot.
                        Are you sure you want to revoke th OAuth token?
                    "
                        onConfirm={onRevokeOAuthToken}>
                        <Button
                            busy={revokeTokenBusy}
                            disabled={disabled || readonly}
                            variant="Type.SECONDARY">
                            Revoke OAuth token
                        </Button>
                    </ConfirmationModal>
                </ResponsiveElementGroup>
            </FormSection>
            <FormSection headline="CRM Limits">
                <ResponsiveElementGroup>
                    <NumberInput
                        width={300}
                        label={<RequiredMark label="HubSpot users"/>}
                        onBlur={changeMaxUsers}
                        readOnly={readonly}
                        placeholder="Max HubSpot users"
                        disabled={disabled}
                        value={limits.maxUsers}
                        maxValue={2147483647}
                        displayedValue={Integer.format(limits.maxUsers, locale)}
                    />
                    <NumberInput
                        width={300}
                        label={<RequiredMark label="Imports & syncs"/>}
                        onBlur={changeMaxImports}
                        readOnly={readonly}
                        disabled={disabled}
                        placeholder="Max imports"
                        value={limits.maxImports || 0}
                        maxValue={2147483647}
                        displayedValue={Integer.format(limits.maxImports || 0, locale)}
                    />
                    <NumberInput
                        width={300}
                        label={<RequiredMark label="Import from PB"/>}
                        onBlur={changeImportCredits}
                        readOnly={readonly}
                        disabled={disabled}
                        placeholder="Import from PB"
                        value={limits.importCredits || 0}
                        maxValue={2147483647}
                        displayedValue={Integer.format(limits.importCredits || 0, locale)}
                    />
                </ResponsiveElementGroup>
            </FormSection>
            <CommonEntitiesSection
                crmType={CrmTypes.HUBSPOTCRM}
                disabled={disabled || readonly}
                isSyncLinkageLoading={isSyncLinkageLoading}
                isLinkageStatisticLoading={isLinkageStatisticLoading}
                linkageStatisticLoadingError={linkageStatisticLoadingError}
                settings={settings}
                orgId={orgId}

                linkageStatistic={linkageStatistic}
                onSyncLinkageStatistic={onSyncLinkageStatistic}
                onChangeSettings={onChangeSettings}
            />
        </Skeleton>
    );
};
